import { computed, watch } from 'vue'

import type { ComputedRef } from 'vue'
import type { ScopeSourceDefinition } from '@ts-scope/interfaces'
import type { ScopeGateArea, ScopeSkeleton, ScopeSource } from '@ts-scope/types'
import type { ScopeStatus } from '@utils/stores/scoped-entry'

import { useDisciplineStore } from '@stores/discipline'

import { DEFAULT_SCOPE_REQUIRES, SCOPE_SOURCES } from '@config/scope/sources'
import { resolveSourceId } from '@utils/scope/source-id'

interface ScopeReadinessReturn {
  requires: ComputedRef<ScopeSource[]>
  status: ComputedRef<ScopeStatus>
  isReady: ComputedRef<boolean>
  isError: ComputedRef<boolean>
  skeleton: ComputedRef<ScopeSkeleton>
  gate: ComputedRef<ScopeGateArea>
  retry: () => void
}

interface ResolvedRequirement {
  definition: ScopeSourceDefinition
  id: number | null
  status: ScopeStatus
}

export const useScopeReadiness = (): ScopeReadinessReturn => {
  const disciplineStore = useDisciplineStore()
  // useRoute() in a persisting layout keeps the previous page's route until the new page resolves
  const route = useRouter().currentRoute

  const requires = computed<ScopeSource[]>(() => {
    if (!route.value.params.kid) return []

    return route.value.meta.scope?.requires ?? DEFAULT_SCOPE_REQUIRES
  })

  const skeleton = computed<ScopeSkeleton>(() => route.value.meta.scope?.skeleton ?? 'demo')

  const gate = computed<ScopeGateArea>(() => route.value.meta.scope?.gate ?? 'page')

  const loads = computed<ScopeSource[]>(() => {
    if (!route.value.params.kid) return []

    return route.value.meta.scope?.loads ?? []
  })

  const resolveRequirement = (source: ScopeSource): ResolvedRequirement => {
    const definition = SCOPE_SOURCES[source]
    const id = resolveSourceId(definition.param, route.value.params, disciplineStore.activeDiscipline?.id ?? null)

    return { definition, id, status: definition.status(id) }
  }

  const requirements = computed<ResolvedRequirement[]>(() => requires.value.map(resolveRequirement))

  const startedSources = computed<ResolvedRequirement[]>(() => [...requires.value, ...loads.value].map(resolveRequirement))

  const isAwaiting = ({ id, status }: ResolvedRequirement): boolean =>
    status === 'loading' || (status === 'idle' && id !== null)

  const status = computed<ScopeStatus>(() => {
    if (requirements.value.some((requirement) => requirement.status === 'error')) return 'error'
    if (requirements.value.some(isAwaiting)) return 'loading'

    return 'ready'
  })

  const isReady = computed<boolean>(() => status.value === 'ready')

  const isError = computed<boolean>(() => status.value === 'error')

  const startWhere = (startable: ScopeStatus): void => {
    startedSources.value
      .filter((requirement) => requirement.status === startable)
      .forEach(({ definition, id }) => {
        if (id !== null) definition.start(id, 'enter')
      })
  }

  const retry = (): void => startWhere('error')

  watch(
    () => startedSources.value.map((requirement) => requirement.id),
    () => startWhere('idle'),
    { immediate: true },
  )

  return {
    requires,
    status,
    isReady,
    isError,
    skeleton,
    gate,
    retry,
  }
}
